/**
 * Venue photos from Google Places.
 *
 * JSON resolution, never an image request to our own API: the returned URI is
 * loaded by the browser directly and unauthenticated, so no session credential
 * can follow an <img> across origins to Google.
 *
 * Nothing here is cached. Google's terms forbid storing the URI, and it expires
 * on its own — a stale one is exactly the case the caller's onError handles.
 */
import { fetchApi } from './apiBase';

export interface PlacePhotoAuthor {
  display_name: string;
  uri: string;
  photo_uri: string;
}

export interface PlacePhoto {
  photo_uri: string;
  author_attributions: PlacePhotoAuthor[];
  google_maps_uri: string;
}

/**
 * How long a row waits before giving up and showing the rung below.
 *
 * A stalled connection never rejects on its own: without a deadline the row
 * shows nothing, never demotes, and holds one of the client's two resolution
 * slots until it unmounts. This is a bound on THIS CALL, enforced by racing a
 * timer — not a promise about how long the server or the transport takes, which
 * neither of them guarantees.
 */
const RESOLVE_TIMEOUT_MS = 8000;

/**
 * Resolve one venue photo. Rejects on every non-success — a missing photo, a
 * throttle, the feature being switched off, a timeout, an abort — because to a
 * caller they all mean the same thing: show the rung below.
 *
 * `signal` lets a caller abandon a resolution it no longer needs, which matters
 * for more than tidiness: the in-flight cap frees the client slot as soon as a
 * row unmounts, so a request left running would go on holding a SERVER slot
 * after the next row had already been handed the client one.
 */
export async function resolvePlacePhoto(
  placeId: string, width = 400, signal?: AbortSignal,
): Promise<PlacePhoto> {
  // An already-abandoned caller gets nothing, and spends nothing.
  if (signal?.aborted) {
    throw new Error('place photo resolution abandoned');
  }

  const params = new URLSearchParams({ place_id: placeId, w: String(width) });
  const controller = new AbortController();

  // Both the deadline AND the caller's abandonment are RACED, not merely
  // forwarded. Forwarding only aborts the transport, and a transport that
  // ignores an abort — a React Native body read does — could resolve long after
  // the row that wanted it was gone, so this promise has to settle on its own
  // terms. The row's ladder treats every rejection the same way: show the rung
  // below.
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;

  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`place photo resolution timed out after ${RESOLVE_TIMEOUT_MS}ms`));
    }, RESOLVE_TIMEOUT_MS);
  });

  const abandoned = new Promise<never>((_, reject) => {
    if (!signal) return;
    onAbort = () => {
      controller.abort();
      reject(new Error('place photo resolution abandoned'));
    };
    signal.addEventListener('abort', onAbort);
  });

  try {
    return await Promise.race([
      fetchApi<PlacePhoto>(
        `/api/v1/plans/place-photo/?${params.toString()}`,
        { signal: controller.signal },
        RESOLVE_TIMEOUT_MS,
      ),
      deadline,
      abandoned,
    ]);
  } finally {
    clearTimeout(timer);
    if (onAbort) signal?.removeEventListener('abort', onAbort);
  }
}
